import { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useApi, ApiError } from "./useApi";
import { showError } from "../lib/feedback";
import { doctorService } from "../services/doctors";
import { billingService } from "../services/billing";
import { useSession } from "../context/SessionContext";
import { Doctor } from "../types/auth.types";
import { AssinaturaStatusResponse } from "../types/billing.types";
import { limparBillingDesatualizado } from "../lib/checkoutIntent";

export type BootstrapStatus =
  | "loading"
  | "ready"
  | "needs-onboarding"
  | "error";

interface SessionBootstrapResult {
  status: BootstrapStatus;
  doctor: Doctor | null;
}

/**
 * Bootstrap único de sessão autenticada. Chama `GET /billing/assinatura`
 * primeiro — é o backend quem resolve a identidade a partir do token
 * (`identidade.get_membro_atual`) e devolve 404 (`db-not-found`) tanto para
 * "médico não existe" quanto para "médico existe mas sem organização"; esse
 * 404 é o único sinal autoritativo de "precisa passar por `POST /onboarding`"
 * (`POST /onboarding` é idempotente pelo `auth0_sub`, então repassar pra lá
 * mesmo em casos ambíguos nunca duplica organização/membro).
 *
 * Só busca o perfil (`GET /doctors/{email}`) depois de confirmar, via
 * billing, que o médico e a organização existem — essa rota é uma consulta
 * legada por e-mail (sem o dependency de identidade), então nunca deve ser
 * o sinal de onboarding pendente por conta própria.
 *
 * Consolida a lógica hoje duplicada — e levemente divergente — entre
 * `CallBackAuth` e `ProtectedRoute`. Roda uma única vez por montagem (guard
 * `useRef`, StrictMode-safe). Passe `skip=true` quando o caller já tem tudo
 * que precisa em contexto (ex.: `ProtectedRoute` navegando entre rotas
 * internas com `metadata.crm` e `billing` já populados).
 *
 * Em caso de sucesso (`status === "ready"`), já popula
 * `SessionContext.metadata`/`billing`/`papel` — os consumidores só decidem para
 * onde navegar a partir do `status`, sem remapear o `Doctor` de novo.
 */
export function useSessionBootstrap(skip = false): SessionBootstrapResult {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const { setMetadata, setBilling, setPapel } = useSession();
  const api = useApi<unknown>();
  const attempted = useRef(false);

  const [status, setStatus] = useState<BootstrapStatus>("loading");
  const [doctor, setDoctor] = useState<Doctor | null>(null);

  useEffect(() => {
    // O React Router reaproveita a mesma instância do ProtectedRoute entre
    // rotas: sem este reset, o próximo bootstrap (depois do onboarding ou do
    // checkout) nunca rodaria e o status antigo ("needs-onboarding") valeria.
    if (skip) {
      attempted.current = false;
      setStatus("loading");
      return;
    }
    if (isLoading || !isAuthenticated || attempted.current) return;
    attempted.current = true;

    const run = async () => {
      const email = user?.email;
      if (!email) {
        // Token sem e-mail: só um login novo resolve.
        showError("unauthorized");
        setStatus("error");
        return;
      }

      // Bypassa .request() de propósito nas duas chamadas abaixo: em ambas
      // precisamos inspecionar o `type` do ApiError synchronously (ex.:
      // db-not-found) pra decidir o próximo passo — o padrão .request()/.error
      // sofre de closure desatualizada logo após o await (o objeto retornado
      // pelo hook não se atualiza dentro da própria função que o chamou).

      let assinatura: AssinaturaStatusResponse | null;
      try {
        assinatura = await billingService.getAssinatura(api.apiFetch);
      } catch (err) {
        const apiError = err as ApiError;
        if (apiError?.type === "db-not-found") {
          setStatus("needs-onboarding");
        } else {
          showError(err);
          setStatus("error");
        }
        return;
      }

      if (!assinatura) {
        setStatus("error");
        return;
      }

      // Free e completo navegam normalmente — o banner/limites tratam o acesso.
      // (arquitetura de pagamentos, §19: dado já gerado nunca fica
      // inacessível). Os componentes que disparam geração é que devem ler
      // `SessionContext.billing.nivel` e desabilitar seus próprios botões.

      let fetchedDoctor: Doctor | null;
      try {
        fetchedDoctor = await doctorService.get(api.apiFetch, email);
      } catch (err) {
        showError(err);
        setStatus("error");
        return;
      }

      if (!fetchedDoctor) {
        // Não deveria acontecer — GET /billing/assinatura já confirmou que
        // o médico existe — mas defensivo contra inconsistência entre as
        // duas fontes.
        setStatus("error");
        return;
      }

      setDoctor(fetchedDoctor);
      setMetadata({
        id: fetchedDoctor.id,
        nome: fetchedDoctor.nome,
        email: fetchedDoctor.email,
        crm: fetchedDoctor.crm,
        especialidade: fetchedDoctor.especialidade,
        total_pacotes_cirurgia: Number(fetchedDoctor.total_pacotes_cirurgia),
        total_pacotes_opme: Number(fetchedDoctor.total_pacotes_opme),
        total_recursos: Number(fetchedDoctor.total_recursos),
        total_resumo_com_referencias: Number(fetchedDoctor.total_resumo_com_referencias),
        role: fetchedDoctor.role,
        companies: fetchedDoctor.companies,
        hospitals: fetchedDoctor.hospitals,
        reservas: fetchedDoctor.reservas,
      });
      setBilling(assinatura);
      if (assinatura.papel) setPapel(assinatura.papel);
      // Checkout acabou de gravar a assinatura — este GET já é o fresco.
      limparBillingDesatualizado();
      setStatus("ready");
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, isLoading, isAuthenticated]);

  return { status, doctor };
}
